import { assert, definedMap } from "@glideapps/ts-necessities";
import { asMaybeString, asRow } from "../../computation-model/data";
import { v4 as uuid } from "uuid";

import { AppKind, SerializedComponent } from "../../components/SerializedApp";
import { rowIndexColumnName } from "../../google-sheets";
import { defined } from "../../Support";
import { makeText } from "../builder-utils";
import {
    AppDescriptionContext,
    ComponentBuilder,
    ComponentConfiguratorContext,
    getAppKindFromAppDescriptionContext,
    PropertyDescriptor,
    RewritingComponentConfiguratorContext,
    WireActionHydrator,
    WireInflationBackend,
} from "../components";
import {
    makeSingleRelationOrThisItemPropertyDescriptor,
    makeTextPropertyDescriptor,
} from "../components/descriptor-utils";
import { PropertySection } from "../components/property-sections";
import {
    getStringProperty,
    getSwitchProperty,
    makeStringProperty,
    makeSwitchProperty,
} from "../components/property-utils";
import {
    ActionDescription,
    ActionKind,
    InputOutputTables,
    MutatingScreenKind,
    PropertyDescription,
    PropertyKind,
    classScreenName,
    getTableName,
    getTableRefTableName,
} from "../description";
import { getMenuScreenNames, titleProperties } from "../description-utils";
import { ActionDescriptor } from "./action-descriptor";
import { DescriptionToken } from "./action-handler";
import { BaseActionHandler } from "./base";
import {
    getSingleRelationOrThis,
    getTableRefForSourceColumnOrThis,
    makeGetterForSourceRelationOrThis,
    makeNavigationPropertyDescriptors,
    makeScreenContextForSourceColumn,
    navigationTargetCurrentPropertyHandler,
    SingleRelationOrThisDescription,
} from "./link-to-screen";

interface PushDetailScreenActionDescription extends ActionDescription, SingleRelationOrThisDescription {
    readonly kind: ActionKind.PushDetailScreen;

    readonly menuID: PropertyDescription | undefined;
    readonly isMenu: PropertyDescription | undefined;

    readonly title: PropertyDescription | undefined;

    readonly navigationTarget: PropertyDescription | undefined;
}

function getIsMenu(desc: PushDetailScreenActionDescription | undefined): boolean {
    return getSwitchProperty(desc?.isMenu) ?? false;
}

export class PushDetailScreenHandler extends BaseActionHandler<PushDetailScreenActionDescription> {
    public readonly kind = ActionKind.PushDetailScreen;

    public readonly iconName = "share";

    public getDescriptor(
        desc: PushDetailScreenActionDescription | undefined,
        tables: InputOutputTables | undefined,
        ccc: ComponentConfiguratorContext,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): ActionDescriptor {
        const properties: PropertyDescriptor[] = [
            makeTextPropertyDescriptor("title", "Title", "Page title", true, mutatingScreenKind, {
                preferredNames: titleProperties,
                propertySection: PropertySection.Design,
                searchable: false,
            }),
            makeSingleRelationOrThisItemPropertyDescriptor("sourceColumn", "Data", PropertySection.Source),
        ];

        if (
            tables !== undefined &&
            (desc === undefined || (getSingleRelationOrThis(desc.sourceColumn) ?? true) === true) &&
            getAppKindFromAppDescriptionContext(ccc) === AppKind.App
        ) {
            // FIXME: Make sure this also works in detail screens
            properties.push({
                kind: PropertyKind.Switch,
                property: {
                    id: "isMenu",
                    get: d => d.isMenu,
                    update: (d, pd, updater) => {
                        const newIsMenu = getSwitchProperty(pd) ?? false;
                        if (newIsMenu && tables.input.rowIDColumn === undefined) {
                            updater?.demandRowIDColumn(
                                getTableName(tables.input),
                                "Without a Row ID Glide can't associate the items in your sheet with the screens in your app."
                            );
                            return {};
                        }
                        let updates: Partial<PushDetailScreenActionDescription> = {
                            isMenu: makeSwitchProperty(newIsMenu),
                        };
                        if (newIsMenu && getStringProperty(d.menuID) === undefined) {
                            updates = { ...updates, menuID: makeStringProperty(uuid()) };
                        }
                        return updates;
                    },
                },
                label: "Independent screen configuration per item",
                section: PropertySection.Data,
                defaultValue: false,
            });
        }

        properties.push(...makeNavigationPropertyDescriptors(ccc, false));

        return { name: "Show Detail Screen", needsScreenContext: true, properties };
    }

    public getScreensUsed(
        desc: PushDetailScreenActionDescription,
        adc: AppDescriptionContext,
        tables: InputOutputTables | undefined
    ): readonly string[] {
        if (getIsMenu(desc)) {
            const screens: string[] = [];

            if (tables !== undefined) {
                screens.push(classScreenName(getTableName(tables.input)));
            }

            if (adc.appDescription === undefined) return screens;

            const menuID = getStringProperty(desc.menuID);
            if (menuID === undefined) return screens;

            screens.push(...getMenuScreenNames(adc.appDescription, menuID));
            return screens;
        } else {
            if (tables === undefined) return [];

            const sourceColumn = getSingleRelationOrThis(desc.sourceColumn) ?? true;
            const tableRef = getTableRefForSourceColumnOrThis(adc, sourceColumn, tables, true);
            if (tableRef === undefined) return [];

            return [classScreenName(getTableRefTableName(tableRef))];
        }
    }

    public makeAction(
        builder: ComponentBuilder,
        desc: PushDetailScreenActionDescription,
        tables: InputOutputTables,
        containingScreenContext: SerializedComponent | undefined,
        _outputContext: SerializedComponent,
        _mutatingScreenKind: MutatingScreenKind | undefined,
        detailScreenTitle: SerializedComponent | undefined,
        itemID: SerializedComponent | undefined
    ): SerializedComponent | undefined {
        const { b } = builder;

        assert(containingScreenContext !== undefined);

        const title =
            definedMap(tables?.input, t =>
                makeText(builder, desc.title, t, true, true, false, false, containingScreenContext)
            ) ?? detailScreenTitle;

        if (getIsMenu(desc)) {
            const menuID = getStringProperty(desc.menuID);
            if (menuID === undefined) return undefined;

            const primaryKeyColumn = tables.input.rowIDColumn ?? rowIndexColumnName;
            const primaryKey = b.getProperty(primaryKeyColumn, defined(containingScreenContext), tables.input);

            return builder.makePushMenuScreen(
                menuID,
                primaryKey,
                classScreenName(getTableName(tables.input)),
                title,
                containingScreenContext
            );
        } else {
            const sourceColumn = getSingleRelationOrThis(desc.sourceColumn) ?? true;
            const screenContext = makeScreenContextForSourceColumn(
                sourceColumn,
                builder,
                tables,
                containingScreenContext,
                true
            );
            if (screenContext === undefined) return undefined;

            return builder.makePushScreen(screenContext.contextTableType, title, itemID, screenContext.screenContext);
        }
    }

    public rewriteAfterReload(
        desc: PushDetailScreenActionDescription,
        tables: InputOutputTables,
        ccc: RewritingComponentConfiguratorContext,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): PushDetailScreenActionDescription | undefined {
        if (getIsMenu(desc)) {
            const menuID = getStringProperty(desc.menuID);
            if (menuID === undefined) return undefined;

            if (!ccc.requireMenuScreen(menuID)) return undefined;
        }

        return super.rewriteAfterReload(desc, tables, ccc, mutatingScreenKind);
    }

    public getTokenizedDescription(
        desc: PushDetailScreenActionDescription,
        _tables: InputOutputTables,
        _ccc: ComponentConfiguratorContext
    ): readonly DescriptionToken[] | undefined {
        const isMenu = getIsMenu(desc);
        return [{ kind: "string", value: isMenu ? "for menu" : "for list" }];
    }

    public inflate(ib: WireInflationBackend, desc: PushDetailScreenActionDescription): WireActionHydrator | undefined {
        // FIXME: implement
        if (getIsMenu(desc)) return undefined;

        const rowGetter = makeGetterForSourceRelationOrThis(ib, desc.sourceColumn, true);
        if (rowGetter === undefined || rowGetter === false) return undefined;

        const [titleGetter] = ib.getValueGetterForProperty(desc.title, true);
        const target = navigationTargetCurrentPropertyHandler.getEnum(desc);

        return vp => {
            const row = definedMap(rowGetter(vp), asRow);
            if (row === undefined) return undefined;

            const title = asMaybeString(titleGetter(vp));

            return async ab => {
                ab.pushDefaultClassScreen(getTableName(ib.tables.input), row, title, target);
                return true;
            };
        };
    }
}
